import { useEffect, useState } from "react";

/**
 * SpotlightOverlay — dims the entire viewport except a rectangular hole
 * around the currently spotlighted DOM element. Also draws a subtle glowing
 * outline around the target so it's unmistakable which feature is being
 * described. If the target is null, renders a full dim scrim (centered card
 * mode used for concept steps like "Welcome").
 *
 * Uses an SVG mask so the "hole" can be perfectly rectangular with rounded
 * corners without interfering with page pointer events (pointer-events: none
 * on the SVG lets users still interact with the highlighted element if the
 * step invites them to).
 */
export default function SpotlightOverlay({ target, padding = 10 }) {
  const [rect, setRect] = useState(null);
  const [viewport, setViewport] = useState({
    w: window.innerWidth,
    h: window.innerHeight,
  });

  useEffect(() => {
    const onResize = () =>
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    // Clear previous rect immediately so the old spotlight doesn't linger
    // while the new target is being located.
    setRect(null);

    if (!target) {
      return undefined;
    }

    let raf = 0;
    let cancelled = false;

    // Poll the target for up to ~2s while pages hydrate/route transitions
    // settle, then keep the rect updated on scroll/resize.
    const findAndTrack = (attempt = 0) => {
      if (cancelled) return;
      const el =
        typeof target === "string" ? document.querySelector(target) : target;
      if (!el) {
        if (attempt < 40) {
          raf = window.setTimeout(() => findAndTrack(attempt + 1), 50);
        }
        return;
      }
      // Scroll target into view first (smooth) so the spotlight lands
      // correctly if the user was elsewhere on the page.
      try {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      } catch {
        /* older browsers */
      }
      const update = () => {
        const r = el.getBoundingClientRect();
        setRect({ x: r.left, y: r.top, w: r.width, h: r.height });
      };
      update();
      // Re-measure a couple more times to catch smooth-scroll settling
      const t1 = window.setTimeout(update, 250);
      const t2 = window.setTimeout(update, 550);
      const onScroll = () => update();
      window.addEventListener("scroll", onScroll, { passive: true, capture: true });
      window.addEventListener("resize", onScroll);
      return () => {
        window.clearTimeout(t1);
        window.clearTimeout(t2);
        window.removeEventListener("scroll", onScroll, true);
        window.removeEventListener("resize", onScroll);
      };
    };

    const cleanup = findAndTrack();
    return () => {
      cancelled = true;
      window.clearTimeout(raf);
      if (typeof cleanup === "function") cleanup();
    };
  }, [target]);

  // No target → simple dim scrim
  if (!target) {
    return (
      <div
        className="fixed inset-0 z-40 bg-black/45 pointer-events-none animate-fade-in"
        data-testid="tutorial-scrim"
        aria-hidden="true"
      />
    );
  }

  // Target not measured yet → nothing to render
  if (!rect) return null;

  const { w: vw, h: vh } = viewport;
  const holeX = Math.max(0, rect.x - padding);
  const holeY = Math.max(0, rect.y - padding);
  const holeW = Math.min(vw - holeX, rect.w + padding * 2);
  const holeH = Math.min(vh - holeY, rect.h + padding * 2);

  return (
    <svg
      className="fixed inset-0 z-40 pointer-events-none"
      width={vw}
      height={vh}
      viewBox={`0 0 ${vw} ${vh}`}
      data-testid="tutorial-spotlight"
      aria-hidden="true"
    >
      <defs>
        <mask id="cd-spot-mask">
          <rect x="0" y="0" width={vw} height={vh} fill="white" />
          <rect
            x={holeX}
            y={holeY}
            width={holeW}
            height={holeH}
            rx="10"
            ry="10"
            fill="black"
          />
        </mask>
      </defs>
      {/* Dim everything except the hole */}
      <rect
        x="0"
        y="0"
        width={vw}
        height={vh}
        fill="rgba(0,0,0,0.55)"
        mask="url(#cd-spot-mask)"
      />
      {/* Glowing outline pulse around the target */}
      <rect
        x={holeX}
        y={holeY}
        width={holeW}
        height={holeH}
        rx="10"
        ry="10"
        fill="none"
        stroke="rgb(224, 168, 46)"
        strokeWidth="2"
        opacity="0.95"
      >
        <animate
          attributeName="stroke-opacity"
          values="0.4;1;0.4"
          dur="1.8s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="stroke-width"
          values="2;3.5;2"
          dur="1.8s"
          repeatCount="indefinite"
        />
      </rect>
    </svg>
  );
}
